<template>
  <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
    <div class="modal-content">
      <div class="modal-header">
        <p class="modal-title">
          <i class="fas fa-database mr-2"></i>
          <span class="font-weight-bold">Account Details</span>
        </p>
        <button type="button" class="close" data-dismiss="modal" aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <div class="modal-body">
        <div class="p-3">
          <div class="form-row">
            <div class="col-7">
              <div class="form-group">
                <label>Custodian</label>
                <input v-model="account.custodian" class="form-control form-control-sm" placeholder="Master"
                  :readonly="account.readonly(`master`)" />
                <small class="form-text text-muted">Account Custodian</small>
              </div>
            </div>
            <div class="col">
              <div class="form-group">
                <label>Number</label>
                <input v-model="account.number" class="form-control form-control-sm" placeholder="Number"
                  :readonly="account.readonly(`number`)" />
                <small class="form-text text-muted">Account Number</small>
              </div>
            </div>
          </div>
          <div class="form-row">
            <div class="col">
              <div class="form-group">
                <label>Name</label>
                <input v-model="account.name" class="form-control form-control-sm" placeholder="Name" />
                <small class="form-text text-muted">Account Name</small>
              </div>
            </div>
          </div>
          <div class="form-row">
            <div class="col">
              <div class="form-group">
                <label>Household</label>
                <searchable-dropdown
                  v-if="account.households && account.households.length"
                  :o="account"
                  p="household"
                  :items="account.households"
                  itemText="name"
                  itemValue="_id"
                  placeholder="Search households..."
                  :disabled="account.disabled('household')"
                />
                <small class="form-text text-muted">Account Household</small>
              </div>
            </div>
          </div>
          <div v-if="account._id" class="form-row">
            <div class="col-4">
              <div class="form-group">
                <label for="accountFundedDate">Account Funded Date</label>
                <date-picker v-model="account.fundedDate" format="MMM D, YYYY" style="  width: 100%;" placeholder=""
                  :disabled-date="notAfterToday"></date-picker>
                <small class="form-text text-muted">Select account funded date</small>
              </div>
            </div>
            <div class="col-4 offset-4">
              <div class="form-group">
                <label>Internal Rep ID</label>
                <searchable-dropdown
                  v-if="account.repcodes"
                  :o="account"
                  p="repCode"
                  :items="account.repcodes"
                  itemText="displayLabel"
                  itemValue="code"
                  placeholder="Search rep codes..."
                />
                <small class="form-text text-muted">Internal Rep ID code</small>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-footer pr-3">
        <div class="text-right footer-controls" :class="{ 'is-ready': account.footerReady }">
          <template v-if="!account.isListedUnderCustodian">
            <span title="Removing an account will also remove it from any contract it is associated with" class="mr-2">
              <i class="fas fa-info-circle text-muted"></i>
            </span>
            <remove-button :button="account.remove"></remove-button>
          </template>
          <template v-else-if="account.isMissingOnCustodian">
            <button type="button" class="btn btn-sm btn-warning ml-2" v-on:click="account.click('archive')">
              <span class="button">Archive</span>
            </button>
          </template>
          <button type="button" class="btn btn-sm btn-primary ml-2" :disabled="account.disabled(`save`)"
            v-on:click="account.click(`save`)">
            <span class="button">Save</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
button {
  width: 80px;
}

p.modal-title i,
p.modal-title span {
  opacity: 0.7;
}

.footer-controls {
  visibility: hidden;
}
.footer-controls.is-ready {
  visibility: visible;
}
</style>

<script>
import { removeButton, searchableDropdown } from "../../../../component";
import { Api, Modal } from "../../../../factory";
import { alert, session } from "../../../../service";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";

export default {
  get components() {
    return {
      removeButton,
      DatePicker,
      searchableDropdown
    };
  },
  props: {
    data: {
      type: Object,
      required: true,
      validator: (o) => o.hasOwnProperty(`account`),
    },
  },
  data() {
    return {
      account: ``,
    };
  },
  created() {
    this.init();
  },
  methods: {
    init() {
      var { data } = this;
      var account = {
        _custodian: (o) => `${o.id} (${o.name || o.type})`,
        _keys: [
          `_id`,
          `contractId`,
          `household`,
          `households`,
          `name`,
          `number`,
          `fundedDate`,
          `archived`,
          `repCode`
        ],
        footerReady: false,
        archivedChanged: false, // Single flag to track if archived status was changed
        provided: [], // keys the caller actually supplied, see the guard in _save()
        async _load() {
          var { account, methods } = data;
          
          try {
            var state = session.get(`state`);
            // console.log(state.custodian);
            var [{ data: custodian }, { data: households }, { data: repcodes }] = await Promise.all(
              [
                Api.get(
                  `custodians/${data.account.custodianId ?? state.custodian._id
                  }`
                ),
                Api.get(`households?_sort=name`),
                Api.get(`repcodes`),
              ]
            );
            this.custodian = this._custodian(custodian);
            this.isListedUnderCustodian = account.isListedUnderCustodian;
            this.isMissingOnCustodian = account.isMissingOnCustodian;
            this.footerReady = true; // Set this after status flags are set to prevent flashing
            this.households = households;
            this.repcodes = repcodes.map(r => ({
              ...r,
              displayLabel: r.name ? `${r.code} (${r.name})` : r.code
            }));
            this.fundedDate = account.fundedDate ? new Date(account.fundedDate) : null;
            this.household = data.household
              ? data.household._id
              : account.householdId
                ? account.householdId
                : ``;
          } catch (e) {
            console.error(e);
            if (methods && typeof methods.error == `function`)
              return methods.error(e);
            alert.error(e.message);
          }
        },
        async _remove() {
          try {
            var { account: a, methods } = data;
            if (a._id) {
              var r = await Api.delete(`accounts/${a._id}`);
              if (methods && typeof methods.success == `function`)
                return data.methods.success(r.data);
            }
            Modal.close();
          } catch (e) {
            console.error(e);
            if (methods && methods.error == `function`) return methods.error(e);
            alert.error(e.message);
          }
        },
        // An absent field is normalised to '' by init(), so writing it back would
        // overwrite the stored value with null. Only round-trip a key when the caller
        // supplied it, or when the user actually put something in it.
        // `archived` is exempt: it is set by an explicit user action, not round-tripped.
        _skip(key) {
          return (
            key !== `archived` &&
            data.account.hasOwnProperty(`_id`) &&
            this.provided.indexOf(key) < 0 &&
            !this[key]
          );
        },
        async _save() {
          var oldHouseholdId = undefined
          this._keys.forEach((key) => {
            if (this._skip(key)) return;
            switch (key) {
              case `_id`:
              case `contractId`:
              case `households`:
                return;
              case `household`:
                // Validate: only save if it's a valid household ID from the list
                if (this[key] && this[key] != data.account.householdId) {
                  var isValidHousehold = this.households.some(h => h._id === this[key]);
                  if (isValidHousehold) {
                    oldHouseholdId = data.account.householdId
                    data.account.householdId = this[key];
                  } else {
                    // Invalid household value, don't update
                    console.warn('Invalid household value ignored:', this[key]);
                  }
                }
                break;
              case `fundedDate`:
                if (this.fundedDate) {
                  data.account[key] = Math.round(this.fundedDate.getTime() / 1000);
                } else {
                  data.account[key] = null; // explicitly save null when cleared
                }
                break;
              case `archived`:
                // Only update archived status if it was explicitly changed by the user
                if (this.archivedChanged) {
                  data.account[key] = true; // Always set to true when archiving
                }
                break;
              case `repCode`:
                // Save selected repCode or null if cleared
                data.account[key] = this.repCode ? this.repCode : null;
                break;
              case `master`:
              case `number`:
                if (!this._id) data.account[key] = this[key];
                break;
              default:
                data.account[key] = this[key];
            }
          });
          var { account, methods } = data;
          if (methods && typeof methods.submit == `function`)
            return data.methods.submit(data);
          try {
            var r = account._id
              ? await Api.put(`accounts/${account._id}`, {...account, oldHouseholdId})
              : await Api.post(`accounts`, account);
            if (methods && typeof methods.success == `function`)
              return data.methods.success(r.data);
            Modal.close();
          } catch (e) {
            console.error(e);
            if (methods && methods.error == `function`) return methods.error(e);
            alert.error(e.message);
          }
        },
        class(type) {
          switch (type) {
            case `remove`:
              return this._delete ? `btn-danger` : `btn-outline-danger`;
            default:
              throw new Error(`Invalid class type, ${type}!`);
          }
        },
        click(type) {
          switch (type) {
            case `remove`:
              return this._remove();
            case `save`:
              return this._save();
            case `archive`:
              this.archivedChanged = true; // Mark that archived status was changed
              this._save(); // Save the updated status
              break;
            default:
              throw new Error(`Invalid click type, ${type}!`);
          }
        },
        custodian: ``,
        isListedUnderCustodian: false,
        isMissingOnCustodian: false,
        disabled(type) {
          switch (type) {
            case `remove`:
              return !data.account._id || data.account.contractId;
            case `save`:
              return !this.household || data.account._id
                ? this.name.length < 1
                : this.number.length < 1 || this.name.length < 1;
            case `household`:
              return data.account.contractId != null;
            default:
              throw new Error(`Invalid disabled type, ${type}!`);
          }
        },
        init() {
          this.none = `-`;
          this.ready = false;
          this.fundedDate = null;
          this.repcodes = null; // null until _load() sets it, so v-if blocks dropdown from mounting early
          // Snapshot which fields the caller supplied. Some list views project only a
          // subset of the account, and an absent field is indistinguishable from an
          // empty one once `|| ''` normalises it below.
          this.provided = this._keys.filter((key) =>
            data.account.hasOwnProperty(key)
          );
          this._keys.forEach((key) => {
            // Skip repCode here - it will be set explicitly after _keys loop
            if (key === 'repCode') return;
            this[key] = data.account[key] || ``;
          });
          // Initialize repCode with existing value before _load() runs
          this.repCode = data.account.repCode || '';
          this.remove = {
            click: () => {
              this._remove();
            },
            disabled: ``,
          };
          this._load();
          return this;
        },
        none: ``,
        readonly(type) {
          switch (type) {
            case `master`:
            case `number`:
              return (
                data.account.hasOwnProperty(`_id`) ||
                data.account.hasOwnProperty(`select`)
              );
            default:
              throw new Error(`Invalid readonly type!`);
          }
        },
        ready: ``,
        remove: ``,
      };
      this.account = account.init();
    },
    notAfterToday(date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return date.getTime() > today.getTime();
    },
  },
};
</script>
